import type { Broadcast, BroadcastStartStopMethod } from '@/models/broadcast';
import type { Category } from '@/models/Category';
import type { Channel, LiveProgram } from '@/models/channel';
import { FuelEntityType, FuelPortalService } from '@/services';
import { buildScheduleDisplayFromBroadcastSchedule } from '@/services/inputSchedule/inputSchedule.service';

/**
 * Data layer for Live Broadcast 2.0 (story 5936).
 *
 * Broadcasts are persisted as LiveProgram entities via Fuel Portal.
 * Destination option lists come from Fuel Portal `Channel` entities.
 *
 * TODO(5936): replace LiveProgram-as-broadcast with a real broadcasts-v2 API when available.
 */

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

const isGuid = (value?: string | null): value is string => !!value && GUID_REGEX.test(value);

const dedupeIds = (values: Array<string | null | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value && value.trim().length > 0)));

export const formatBroadcastTagsForForm = (tags: string[] | string | null | undefined): string => {
  if (Array.isArray(tags)) {
    return tags
      .map((tag) => tag.trim())
      .filter(Boolean)
      .join(', ');
  }
  if (typeof tags === 'string') {
    return tags.trim();
  }
  return '';
};

export const normalizeBroadcastTags = (tags: string[] | string | null | undefined): string[] => {
  let values: string[];
  if (Array.isArray(tags)) {
    values = tags;
  } else if (typeof tags === 'string') {
    values = tags.split(',');
  } else {
    values = [];
  }

  const normalized: string[] = [];
  values.forEach((tag) => {
    const trimmed = tag.trim();
    if (trimmed && !normalized.includes(trimmed)) {
      normalized.push(trimmed);
    }
  });
  return normalized;
};

const resolveBroadcastCategory = (
  categoryName: string | null | undefined,
  ouCategories: Category[] | null | undefined
): string[] | Category[] => {
  const trimmed = categoryName?.trim() ?? '';
  if (!trimmed) {
    return [];
  }

  const matched = (ouCategories ?? []).find(
    (category) => !category.isDeleted && category.enabled !== false && category.name?.trim() === trimmed
  );
  if (matched) {
    return [matched];
  }

  return [trimmed];
};

export interface DestinationOption {
  value: string;
  label: string;
}

/** Form-local start/stop method derived from persisted hasSCTE + scheduleDisplay. */
export const deriveStartStopMethodFromLiveProgram = (program: LiveProgram | null | undefined): BroadcastStartStopMethod => {
  if (program?.hasSCTE) {
    return 'SCTE-35';
  }

  const scheduleDisplay = program?.metadata?.scheduleDisplay;
  const status = scheduleDisplay?.status;
  const startTime = scheduleDisplay?.startTime?.toString().trim() ?? '';
  const endTime = scheduleDisplay?.endTime?.toString().trim() ?? '';
  if (status === 'Scheduled' || startTime.length > 0 || endTime.length > 0) {
    return 'Schedule';
  }

  return 'Manual';
};

export type StartStopMethodDeriveInput = {
  hasSCTE?: boolean;
  scheduleDisplayStatus?: string | null;
  scheduleDisplayStartTime?: string | null;
  scheduleDisplayEndTime?: string | null;
};

export const deriveStartStopMethodFromFlags = (input: StartStopMethodDeriveInput): BroadcastStartStopMethod => {
  if (input.hasSCTE) {
    return 'SCTE-35';
  }

  const startTime = input.scheduleDisplayStartTime?.trim() ?? '';
  const endTime = input.scheduleDisplayEndTime?.trim() ?? '';
  if (input.scheduleDisplayStatus === 'Scheduled' || startTime.length > 0 || endTime.length > 0) {
    return 'Schedule';
  }

  return 'Manual';
};

const mergeOptionsByValue = (baseOptions: DestinationOption[], additionalOptions: DestinationOption[]): DestinationOption[] => {
  const merged = new Map<string, DestinationOption>();

  baseOptions.forEach((option) => {
    if (!option?.value) return;
    merged.set(option.value, option);
  });

  additionalOptions.forEach((option) => {
    if (!option?.value) return;
    const existing = merged.get(option.value);
    if (!existing || !existing.label) {
      merged.set(option.value, option);
    }
  });

  return Array.from(merged.values());
};

export type ManualLiveChangeEvent = {
  broadcastId: string;
  isLive: boolean;
};

type ManualLiveListener = (event: ManualLiveChangeEvent) => void;

const manualLiveListeners = new Set<ManualLiveListener>();

export const subscribeManualLive = (listener: ManualLiveListener): (() => void) => {
  manualLiveListeners.add(listener);
  return () => {
    manualLiveListeners.delete(listener);
  };
};

export type SaveBroadcastFlowScaffoldContext = {
  isNew: boolean;
  previousInputSourceId?: string | null;
  nextInputSourceId?: string | null;
  timezone?: string;
  /** Form-local only; persisted as hasSCTE + scheduleDisplay + invertSCTE. */
  startStopMethod: BroadcastStartStopMethod;
  scteInvert: boolean;
  ouCategories?: Category[];
};

export const saveBroadcastFlowScaffold = async (
  broadcast: Broadcast,
  context: SaveBroadcastFlowScaffoldContext
): Promise<Broadcast> => {
  const nextInputId = context.nextInputSourceId ?? '';
  const { startStopMethod } = context;
  const isScteMethod = startStopMethod === 'SCTE-35';

  const candidateId = isGuid(broadcast.id) ? broadcast.id : null;
  let existingLiveProgram: LiveProgram | null = null;
  if (candidateId) {
    existingLiveProgram = await FuelPortalService.getEntityById<LiveProgram>(FuelEntityType.LiveProgram, candidateId, {
      fullInfo: true,
    });
  }

  // Schedule data pulled in-memory from the selected input source; persisted (minus index)
  // onto the live program's scheduleDisplay. Preserve any existing index/metadata on update.
  const mappedScheduleDisplay = buildScheduleDisplayFromBroadcastSchedule(
    broadcast.schedule,
    context.timezone ?? '',
    startStopMethod
  );
  const existingScheduleDisplay = existingLiveProgram?.metadata?.scheduleDisplay;
  const scheduleDisplay = mappedScheduleDisplay
    ? {
        ...existingScheduleDisplay,
        ...mappedScheduleDisplay,
        ...(existingScheduleDisplay?.index != null ? { index: existingScheduleDisplay.index } : {}),
      }
    : existingScheduleDisplay;

  const categories = resolveBroadcastCategory(broadcast.category, context.ouCategories);
  const tags = normalizeBroadcastTags(broadcast.tags);
  const thumbnailPreviewUri = (broadcast.thumbnail ?? '').trim();
  const existingThumbnail = existingLiveProgram?.metadata?.thumbnail;
  const thumbnailMetadata = thumbnailPreviewUri
    ? { ...existingThumbnail, previewUri: thumbnailPreviewUri }
    : null;

  const liveProgramPayload: LiveProgram = {
    ...(existingLiveProgram ?? {}),
    ...(candidateId ? { id: candidateId } : {}),
    ouId: broadcast.ouId,
    entityType: FuelEntityType.LiveProgram,
    name: broadcast.title,
    description: broadcast.description ?? existingLiveProgram?.description ?? '',
    enabled: existingLiveProgram?.enabled ?? true,
    inputId: nextInputId || null,
    hasSCTE: isScteMethod,
    hasGeofencing: Boolean(broadcast.hasGeofencing),
    zipcodes: broadcast.hasGeofencing ? broadcast.zipcodes ?? '' : '',
    channelIds: dedupeIds(broadcast.destinations?.linearChannels ?? []),
    metadata: {
      ...existingLiveProgram?.metadata,
      invertSCTE: isScteMethod && Boolean(context.scteInvert),
      categories,
      tags,
      thumbnail: thumbnailMetadata,
      ...(scheduleDisplay ? { scheduleDisplay } : {}),
    } as LiveProgram['metadata'],
  };

  const savedLiveProgram = await FuelPortalService.upsertEntity<LiveProgram>(FuelEntityType.LiveProgram, liveProgramPayload);
  const liveProgramId = savedLiveProgram?.id ?? candidateId;

  // LiveProgram upsert now owns Input ICS + relations in fueltools
  // (CheckUpsertLiveProgram / OnAfterUpsert). Keep sending inputId: nextInputId
  // (including Unassigned = OU id).
  // #region UI_OWNED_INPUT_ICS_AND_RELATIONS
  // Restore imports when commenting this region back in:
  //   import type { Input } from '@/models/input';
  //   removeLiveProgramFromInputSchedule, syncInputScheduleForBroadcast from inputSchedule.service
  // const previousInputId = context.previousInputSourceId ?? '';
  // const inputSourceChanged = previousInputId !== nextInputId;
  // if (isGuid(liveProgramId) && context.timezone) {
  //   const { timezone } = context;
  //   if (inputSourceChanged && previousInputId) {
  //     await removeLiveProgramFromInputSchedule(previousInputId, liveProgramId, timezone);
  //   }
  //   if (nextInputId) {
  //     await syncInputScheduleForBroadcast(nextInputId, broadcast, liveProgramId, timezone, startStopMethod);
  //   }
  // }
  //
  // if (isGuid(liveProgramId)) {
  //   // Keep writes bounded to at most two inputs: previous + next.
  //   const targetInputIds = dedupeIds([previousInputId, nextInputId]);
  //   const inputUpdates = await Promise.all(
  //     targetInputIds.map(async (inputId) => {
  //       const currentInput = await FuelPortalService.getEntityById<Input>(FuelEntityType.Input, inputId, { fullInfo: true });
  //       if (!currentInput?.id) return null;
  //
  //       const originalIds = dedupeIds(currentInput.liveProgramIds ?? []);
  //       let nextIds = originalIds;
  //
  //       if (inputSourceChanged && inputId === previousInputId) {
  //         nextIds = originalIds.filter((id) => id !== liveProgramId);
  //       }
  //       if (inputId === nextInputId) {
  //         nextIds = dedupeIds([...nextIds, liveProgramId]);
  //       }
  //
  //       if (nextIds.join('|') === originalIds.join('|')) {
  //         return null;
  //       }
  //
  //       return {
  //         ...currentInput,
  //         liveProgramIds: nextIds,
  //       } as Input;
  //     })
  //   );
  //
  //   const toPersist = inputUpdates.filter((item): item is Input => !!item);
  //   if (toPersist.length > 0) {
  //     await Promise.all(toPersist.map((input) => FuelPortalService.upsertEntity<Input>(FuelEntityType.Input, input)));
  //   }
  // }
  // #endregion UI_OWNED_INPUT_ICS_AND_RELATIONS

  const liveProgramThumbnailPreview =
    savedLiveProgram?.metadata?.thumbnail?.previewUri ?? existingLiveProgram?.metadata?.thumbnail?.previewUri ?? '';

  return {
    ...broadcast,
    id: liveProgramId ?? broadcast.id,
    category: broadcast.category,
    tags,
    thumbnail: liveProgramThumbnailPreview || broadcast.thumbnail || '',
    thumbnailSrc: liveProgramThumbnailPreview || broadcast.thumbnail || null,
    ingestFile: null,
  };
};

export const listLinearChannelOptions = async (ouId: string): Promise<DestinationOption[]> => {
  if (!ouId) return [];
  try {
    const result = await FuelPortalService.getList(FuelEntityType.Channel, {
      enabled: true,
      ouId,
      page: 0,
      size: 500,
      sortBy: 'name',
      orderBy: 'asc',
      showDeleted: false,
      useCompactChannel: true,
      excludeSubtype: ['MediaLibrary'],
      showLiveEnabled: true,
    });
    const channels: Channel[] = result?.data ?? [];
    return channels.map((channel) => ({ value: channel.id, label: channel.name }));
  } catch (ex) {
    console.error('Unable to load linear channel options', { ex });
    return [];
  }
};

export const hydrateSelectedLinearChannelOptions = async (
  selectedChannelIds: string[],
  existingOptions: DestinationOption[]
): Promise<DestinationOption[]> => {
  const uniqueSelectedIds = dedupeIds(selectedChannelIds);
  if (uniqueSelectedIds.length === 0) {
    return existingOptions;
  }

  const optionValues = new Set(existingOptions.map((option) => option.value));
  const missingIds = uniqueSelectedIds.filter((id) => !optionValues.has(id));
  if (missingIds.length === 0) {
    return existingOptions;
  }

  try {
    const channels = await FuelPortalService.batchGet<Channel[]>(FuelEntityType.Channel, missingIds);
    const hydratedOptions = (Array.isArray(channels) ? channels : [])
      .filter((channel): channel is Channel & { id: string } => Boolean(channel?.id))
      .map((channel) => ({
        value: channel.id,
        label: channel.name || channel.id,
      }));

    return mergeOptionsByValue(existingOptions, hydratedOptions);
  } catch (ex) {
    console.error('Unable to hydrate selected linear channel options', { ex });
    return existingOptions;
  }
};
